import StudioContactStart, { type StudioContact } from './StudioContactStart';
import {customPurchaseLayout} from './custom-purchase-layout';
import { consultationRoomSnapshots } from "./consultative-flow";
import BlissConsultativeJourney from './BlissConsultativeJourney';
import type {ConfiguratorSnapshot} from './design/configurator/integration';
import {quickStartLeadPayload} from '../../utils/quickStartLeadPayload';
import {savedSleepGoal} from './sleep-capacity';
import {captureStudioLead} from '../../services/studioLeadService';
import {syncFunnelLeadToSalesInbox} from '../../services/funnelLeadSyncService';
import {requestDeadline} from '../../utils/requestDeadline';
import {customerRoomName} from '../../../shared/customerRoomName';
import type {OrderConsent} from '../../../shared/orderConsent';
import BunkSetupWizard from './BunkSetupWizard';
import CompactPricing from './CompactPricing';
import {numberPurchaseBunks,placePurchaseBunk,type BunkSource,type BunkDestination} from './photo/bunk-destination';
import {activeRoomView,roomViews,updateRoomView,withRoomViews,detachViewBunk,draftBunks,purchaseRoomSnapshot,restorePurchaseRooms,savedPurchaseRooms,type PhotoPurchaseRoom} from './photo/photo-purchase-rooms';
import {reconcilePhotoBunks} from './photo/photo-draft-selection';
export {photoPurchaseSnapshot} from './photo/photo-purchase-snapshot';
import type {BunkSelection,BunkFinish} from './BunkOptions';
import {hasBasicUpgrades,type BasicUpgrades} from '../../../shared/basicUpgrades';
import BlissQuickStart, {quickChoices,quickBunks,type QuickAnswers} from './BlissQuickStart';
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { BED_OPTIONS } from "./design/configurator/room/beds";
import { estimatePlanRange } from "./design/configurator/room/quote-range";
import { getPricing } from "../../data/pricing";
import {
  fetchBlissRoomPlan,
  saveBlissRoomPlan,
} from "../../services/blissRoomPlanService";
import type { BlissRoomPlanPayload } from "../../config/blissRoomPlan";
import type { PhotoPreviewDraft } from "./photo/RoomPhotoPreview";
import RoomReviewCheckout from "./RoomReviewCheckout";
import { paidCalendlySchedulePath } from "../../config/calendlySchedule";
import "./BlissPurchaseJourney.css";
const Photo = lazy(() => import("./photo/RoomPhotoPreview"));
const Checkout = lazy(() => import("./PlanCheckoutDialog"));
const Planner = lazy(() => import("./design/BlissDesignConfiguratorShell"));
const DRAFT = "bliss_photo_purchase_draft";
const PROJECT = "bliss_photo_purchase_rooms_v1";
function readProject(){
  try {
    const project=JSON.parse(sessionStorage.getItem(PROJECT)||'null');
    if(project?.version===1&&Array.isArray(project.rooms)&&project.rooms.length>0&&project.rooms.length<=20&&project.rooms.every((r:PhotoPurchaseRoom)=>r.id&&r.name&&r.bunks?.length&&r.bunks.every(b=>BED_OPTIONS[b.choice])))return {...project,rooms:numberPurchaseBunks(project.rooms)} as {version:1;rooms:PhotoPurchaseRoom[];activeRoomId:string;planId?:string;contact?:BlissRoomPlanPayload['contact'];mode:'photo'|'order';photosOmitted?:boolean};
  }catch{}
  return undefined;
}
export default function BlissPurchaseJourney() {
  const location = useLocation(),
    navigate = useNavigate(),
    params = new URLSearchParams(location.search);
  const [resumeProject]=useState(()=>{const stored=readProject();return params.get('plan_id')&&stored?.planId===params.get('plan_id')?stored:undefined});
  const resume=useRef(resumeProject);
  const startingRoom=resume.current?.rooms.find(r=>r.id===resume.current?.activeRoomId)??resume.current?.rooms[0];
  const startingView=startingRoom?activeRoomView(startingRoom):undefined;
  const rooms=useRef<PhotoPurchaseRoom[]>(resume.current?.rooms??[]);
  const [activeRoomId,setActiveRoomId]=useState(startingRoom?.id??'room-1');
  const activeRoom=useRef(activeRoomId);
  const [activeViewId,setActiveViewId]=useState(startingView?.id??'view-1');
  const activeView=useRef(activeViewId);
  const [,refreshRooms]=useState(0);
  const switching=useRef(false);
  const [setup,setSetup]=useState<{bunk:BunkSelection;number:number;source?:BunkSource}|null>(null);
  const [photoRevision,setPhotoRevision]=useState(0);
  const photoGeneration=useRef(0);
  function resetPhoto(){setPhotoRevision(++photoGeneration.current)}
  const draftTimer = useRef<number | undefined>(undefined);
  const [mode, setMode] = useState<
      "welcome" | "photo" | "order" | "review" | "review-paid" | "workspace"
    >(resume.current&&(resume.current.rooms.length>1||!!startingRoom?.views)?resume.current.mode:"welcome"),
    [access, setAccess] = useState(false),
    [checking, setChecking] = useState(true),
    [error, setError] = useState(""),
    [attempt, setAttempt] = useState(0);
  const [planId, setPlanId] = useState(params.get("plan_id") || resume.current?.planId || undefined),
    [base, setBase] = useState<BlissRoomPlanPayload>({contact:resume.current?.contact}),
    [checkout, setCheckout] = useState(false),
    [saving, setSaving] = useState(false);
  const draft = useRef(startingRoom ? startingView?.photoPreview : undefined),
    [initialDraft, setInitialDraft] = useState(draft.current),
    choices = useRef<number[]>(startingView?.bunks.map(b=>b.choice) ?? draft.current?.beds.map((b) => b.choice) ?? [0]);
  const [selectedChoices,setSelectedChoices]=useState<number[]>(choices.current);
  const [colorCodes,setColorCodes]=useState<(string|undefined)[]>(startingView?.bunks.map(b=>b.customColorCode)??draft.current?.beds.map(b=>b.customColorCode)??[]);
  const [finishes,setFinishes]=useState<BunkFinish[]>(startingView?.bunks.map(b=>b.finish)??draft.current?.beds.map(b=>b.finish??'gloss_white')??[]);
  const upgrades=useRef<BasicUpgrades[]>(startingView?.bunks.map(b=>b.upgrades)??draft.current?.beds.map(b=>b.upgrades??{})??[]);
  const [,refreshUpgrades]=useState(0);
  function setUpgrades(next:BasicUpgrades[]=[]){
    upgrades.current=next;refreshUpgrades(n=>n+1);
    if(draft.current){const updated={...draft.current,beds:draft.current.beds.map((b,i)=>({...b,upgrades:next[i]??{}}))};draft.current=updated;setInitialDraft(updated);try{sessionStorage.setItem(DRAFT,JSON.stringify(updated))}catch{}}
  }
  const [initialChoice,setInitialChoice]=useState(0);

  const [preferredChoices,setPreferredChoices]=useState<number[]>([]);
  const [initialUpgrades,setInitialUpgrades]=useState<Record<number,BasicUpgrades>>({});
  const [initialBunks,setInitialBunks]=useState<BunkSelection[]>(startingView?.bunks??[]);
  function currentRooms(selected=choices.current,extras=upgrades.current):PhotoPurchaseRoom[]{
    const room=rooms.current.find(r=>r.id===activeRoom.current);
    const bunks:BunkSelection[]=selected.map((choice,i)=>({choice,finish:(mode==='photo'?draft.current?.beds[i]?.finish:undefined)??finishes[i]??'gloss_white',customColorCode:(mode==='photo'?draft.current?.beds[i]?.customColorCode:undefined)??colorCodes[i],upgrades:extras[i]??{}}));
    const baseRoom=room??{id:activeRoom.current,name:'Bunk room 1',bunks};
    const current=updateRoomView(baseRoom,{...activeRoomView(baseRoom),bunks,photoPreview:mode==='order'&&draft.current?reconcilePhotoBunks(draft.current,bunks):draft.current});
    return numberPurchaseBunks(rooms.current.length?rooms.current.map(r=>r.id===current.id?current:r):[current]).map(r=>({...r,name:customerRoomName(base.contact?.name,r.name)}));
  }
  function orderSnapshots(selected=choices.current,extras=upgrades.current){return currentRooms(selected,extras).map(purchaseRoomSnapshot)}
  function remember(next:PhotoPurchaseRoom[],id=activeRoom.current,savedId=planId,nextMode:'photo'|'order'=mode==='order'?'order':'photo'){
    window.clearTimeout(draftTimer.current);
    const project={version:1,rooms:next,activeRoomId:id,planId:savedId,contact:base.contact,mode:nextMode};
    try{if(next.length>1||next.some(r=>r.views))sessionStorage.removeItem(DRAFT);sessionStorage.setItem(PROJECT,JSON.stringify(project))}catch{
      // Keep a server-resume pointer even when multiple room photos exceed session storage.
      if(savedId)try{sessionStorage.setItem(PROJECT,JSON.stringify({...project,photosOmitted:true,rooms:next.map(({photoPreview,views,...room})=>({...room,...views?{views:views.map(({photoPreview,...view})=>view)}:{}}))}))}catch{}
    }
  }
  function loadRoom(room:PhotoPurchaseRoom,nextMode:'photo'|'order'=mode==='order'?'order':'photo',savedId=planId){
    window.clearTimeout(draftTimer.current);activeRoom.current=room.id;setActiveRoomId(room.id);
    const view=activeRoomView(room);activeView.current=view.id;setActiveViewId(view.id);
    draft.current=view.photoPreview;setInitialDraft(view.photoPreview);
    choices.current=view.bunks.map(b=>b.choice);setSelectedChoices(choices.current);
    upgrades.current=view.bunks.map(b=>b.upgrades);setFinishes(view.bunks.map(b=>b.finish));setColorCodes(view.bunks.map(b=>b.customColorCode));
    setInitialBunks(view.bunks);setInitialChoice(view.bunks[0].choice);setPreferredChoices(choices.current);setInitialUpgrades({});
    setMode(nextMode);setError('');remember(rooms.current,room.id,savedId,nextMode);window.scrollTo(0,0);
  }
  async function changeRoom(id:string,viewId?:string,bedId?:number){
    if(switching.current||(id===activeRoom.current&&(!viewId||viewId===activeView.current)))return;
    switching.current=true;setSaving(true);setError('');
    try{
      const next=currentRooms().map(r=>{
        if(r.id!==id||!viewId)return r;
        const views=roomViews(r).map(v=>v.id===viewId&&v.photoPreview&&bedId!==undefined?{...v,photoPreview:{...v.photoPreview,active:bedId}}:v);
        return withRoomViews(r,views,viewId);
      });
      const savedId=await persist(choices.current,next,id);loadRoom(next.find(r=>r.id===id)!,mode==='order'?'order':'photo',savedId);
    }catch(e){setError((e as Error).message)}finally{switching.current=false;setSaving(false)}
  }
  async function removeViewBunk(index:number){
    if(switching.current)return;
    const next=currentRooms(),room=next.find(r=>r.id===activeRoom.current)!;
    if(room.bunks.length<2)return;
    switching.current=true;setSaving(true);setError('');
    try{
      const {views}=detachViewBunk(room,index),target=withRoomViews(room,views,views[0].id);
      const all=next.map(r=>r.id===room.id?target:r),savedId=await persist(choices.current,all,target.id);
      loadRoom(target,'photo',savedId);
    }catch(e){setError((e as Error).message)}finally{switching.current=false;setSaving(false)}
  }
  async function removeRoom(){
    if(switching.current||rooms.current.length<2)return;
    switching.current=true;setSaving(true);setError('');
    try{const next=currentRooms().filter(r=>r.id!==activeRoom.current);const savedId=await persist(choices.current,next,next[0].id);loadRoom(next[0],mode==='order'?'order':'photo',savedId)}catch(e){setError((e as Error).message)}finally{switching.current=false;setSaving(false)}
  }
  function renameRoom(name:string){
    const next=currentRooms().map(r=>r.id===activeRoom.current?{...r,name:name.trim()||r.name}:r);rooms.current=next;refreshRooms(n=>n+1);remember(next);
  }
  function checkoutPackage(){return currentRooms().some(r=>r.bunks.some(b=>hasBasicUpgrades(b.upgrades)))?'premier':'standard'}
  function validateOrder(){for(const room of currentRooms())if(room.bunks.some(b=>b.finish==='custom_color'&&b.customColorCode&&!/^SW\d{4}$/.test(b.customColorCode)))throw Error(`${room.name}: enter a four-digit Sherwin-Williams code, such as SW1234, or leave it blank.`)}
  const roomNavigation=rooms.current.length>1?<div className="bpj-room-nav" aria-label="Rooms on your order"><label>Room<select aria-label="Current room" value={activeRoomId} disabled={saving} onChange={e=>void changeRoom(e.target.value)}>{rooms.current.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}</select></label><details><summary>Room details</summary><label>Room name<input key={activeRoomId} aria-label="Room name" maxLength={40} defaultValue={rooms.current.find(r=>r.id===activeRoomId)?.name} disabled={saving} onBlur={e=>renameRoom(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')e.currentTarget.blur()}}/></label><button type="button" disabled={saving} onClick={()=>void removeRoom()}>Remove this room</button></details>{saving&&<small role="status">Saving your rooms…</small>}</div>:null;
  const visibleRooms=currentRooms();
  const currentRoom=visibleRooms.find(r=>r.id===activeRoomId)??visibleRooms[0];
  const currentView=activeRoomView(currentRoom);
  const photoContext=`${currentRoom.name} · ${currentView.name==='Original spot'?'This wall':currentView.name}`;
  function beginSetup(source?:BunkSource){
    if(switching.current)return;
    const all=currentRooms();rooms.current=all;setInitialDraft(draft.current);remember(all);
    const room=all.find(r=>r.id===(source?.roomId??activeRoom.current))!;
    const view=source?roomViews(room).find(v=>v.id===source.viewId)!:activeRoomView(room);
    const index=source?.index??Math.max(0,view.photoPreview?.beds.findIndex(b=>b.id===view.photoPreview?.active)??0);
    const number=source?view.bunkNumbers![index]:Math.max(0,...all.flatMap(r=>roomViews(r).flatMap(v=>v.bunkNumbers??[])))+1;
    resetPhoto();setSetup({bunk:view.bunks[index],number,source});window.scrollTo(0,0);
  }
  async function completeSetup(bunk:BunkSelection,destination:BunkDestination){
    if(!setup)return;
    const result=placePurchaseBunk(rooms.current,bunk,destination,setup.source);
    const savedId=await persist(choices.current,result.rooms,result.target.id);
    setSetup(null);resetPhoto();loadRoom(result.target,mode==='order'||!activeRoomView(result.target).photoPreview?.photo?'order':'photo',savedId);
  }
  function bunkNavigation(active:number,onSelect:(id:number)=>void){
    return <div className="bpj-bunk-navigation" aria-label="Bunks on your order"><div role="group" aria-label="Choose a bunk">{visibleRooms.flatMap(room=>roomViews(room).flatMap(view=>view.bunks.map((b,i)=>{
      const id=view.photoPreview?.beds[i]?.id??i,selected=room.id===activeRoomId&&view.id===activeViewId&&id===active;
      return <button key={`${room.id}:${view.id}:${id}`} type="button" disabled={saving} aria-pressed={selected} onClick={()=>!view.photoPreview?.photo?beginSetup({roomId:room.id,viewId:view.id,index:i}):room.id===activeRoomId&&view.id===activeViewId?onSelect(id):void changeRoom(room.id,view.id,id)}><strong>Bunk {view.bunkNumbers?.[i]}</strong><small>{visibleRooms.length>1?room.name:BED_OPTIONS[b.choice].name}{!view.photoPreview?.photo?' · Choose location':''}</small></button>;
    }))) }<button type="button" disabled={saving} className="bpj-add-bunk" onClick={()=>beginSetup()}>+ Add bunk</button></div></div>;
  }
  function changePricedBunk(source:BunkSource,bunk:BunkSelection){
    if(switching.current)return;
    const all=currentRooms().map(room=>{
      if(room.id!==source.roomId)return room;
      const views=roomViews(room).map(view=>{
        if(view.id!==source.viewId)return view;
        const bunks=view.bunks.map((prior,index)=>index===source.index?bunk:prior);
        return {...view,bunks,photoPreview:view.photoPreview?{...reconcilePhotoBunks(view.photoPreview,bunks),step:view.photoPreview.step}:undefined};
      });
      return room.views?withRoomViews(room,views,room.activeViewId??views[0].id):{...room,bunks:views[0].bunks,photoPreview:views[0].photoPreview};
    });
    rooms.current=all;
    const active=activeRoomView(all.find(room=>room.id===activeRoom.current)!);
    draft.current=active.photoPreview;setInitialDraft(active.photoPreview);
    choices.current=active.bunks.map(b=>b.choice);setSelectedChoices(choices.current);
    upgrades.current=active.bunks.map(b=>b.upgrades);setFinishes(active.bunks.map(b=>b.finish));setColorCodes(active.bunks.map(b=>b.customColorCode));
    setInitialBunks(active.bunks);refreshRooms(n=>n+1);remember(all);
  }
  async function removePricedBunk(source:BunkSource){
    const all=currentRooms();if(switching.current||all.reduce((count,room)=>count+room.bunks.length,0)<2)return;
    switching.current=true;setSaving(true);setError('');
    try{
      const next=all.flatMap(room=>{
        if(room.id!==source.roomId)return [room];
        const {views}=detachViewBunk(withRoomViews(room,roomViews(room),source.viewId),source.index);
        return views.length?[withRoomViews(room,views,views.some(v=>v.id===room.activeViewId)?room.activeViewId!:views[0].id)]:[];
      });
      let target=next.find(room=>room.id===activeRoom.current)??next[0];
      const view=activeRoomView(target);
      if(view.photoPreview)target=updateRoomView(target,{...view,photoPreview:{...view.photoPreview,step:5}});
      const result=next.map(room=>room.id===target.id?target:room),savedId=await persist(choices.current,result,target.id);
      resetPhoto();loadRoom(target,view.photoPreview?'photo':'order',savedId);
    }catch(e){setError((e as Error).message)}finally{switching.current=false;setSaving(false)}
  }
  function leavePricing(step:1|4=4){
    if(draft.current?.photo){
      const updated={...draft.current,step};changeDraft(updated);setInitialDraft(updated);resetPhoto();setMode('photo');
    }else setMode('welcome');
    window.scrollTo(0,0);
  }
  const [quoteContactOpen,setQuoteContactOpen]=useState(false);
  const [quoteRequested,setQuoteRequested]=useState(false);
  const [orderConsent,setOrderConsent]=useState<OrderConsent>();
  async function approveOrder(consent:OrderConsent){
    if(switching.current)return;
    switching.current=true;setSaving(true);setError('');
    try{validateOrder();await persist();setOrderConsent(consent);setCheckout(true)}catch(e){setError((e as Error).message)}finally{switching.current=false;setSaving(false)}
  }
  const introduction = useRef({base,planId});
  introduction.current = {base,planId};
  const introductionSave = useRef(Promise.resolve());
  function saveIntroduction(contact:NonNullable<BlissRoomPlanPayload['contact']>,answers:QuickAnswers,answeredThrough=5,event:'progress'|'custom_inquiry'='progress'):Promise<void>{
    // Serialize saves so a slow earlier step cannot replace newer customer answers.
    const task=introductionSave.current.catch(()=>{}).then(async()=>{
      const current=introduction.current;
      let quoteRequestId=String(current.base.attribution?.quoteRequestId||params.get('bliss_quote_id')||'');
      if(!quoteRequestId){
        quoteRequestId=await captureStudioLead(contact,{notifyTeam:true});
        current.base={...current.base,attribution:{...current.base.attribution,quoteRequestId}};
        introduction.current=current;setBase(current.base);
      }
      const completedThrough=Math.max(answeredThrough,Number((current.base.layout as any)?.quickAnswers?.answeredThrough)||0);
      const isCustom=answers.productLine==='custom';
      const customSubmitted=isCustom&&event==='custom_inquiry';
      const quickAnswers={...answers,customSubmitted,answeredThrough:completedThrough,bunks:!isCustom&&completedThrough>=3?quickBunks(answers).map(b=>({...b,modelName:BED_OPTIONS[b.choice].name})):[],configurationPreferences:isCustom?[]:quickChoices(answers).map(choice=>BED_OPTIONS[choice].name)};
      const result=await saveBlissRoomPlan({...current.base,contact,attribution:{...current.base.attribution,quoteRequestId,source:'photo_tool'},layout:{...current.base.layout,quickAnswers:{...quickAnswers,customSubmitted:false}} as any},current.planId);
      if(!result.plan)throw Error(result.error||'Could not save your project. Please retry.');
      introduction.current={base:result.plan,planId:result.plan.id};setBase(result.plan);setPlanId(result.plan.id);
      const sync=await syncFunnelLeadToSalesInbox(quickStartLeadPayload({quoteRequestId,planId:result.plan.id,contact,answers:quickAnswers,completedThrough,submitted:customSubmitted}));
      if(!sync.success)throw Error('Your room is saved, but your preferences could not sync. Please try again.');
      if(customSubmitted){
        // A saved confirmation is only valid after the Sales handoff succeeds.
        const confirmed=await saveBlissRoomPlan({...result.plan,layout:{...result.plan.layout,quickAnswers} as any},result.plan.id);
        if(!confirmed.plan)throw Error(confirmed.error||'Your request reached Bliss, but the confirmation could not save. Please retry.');
        introduction.current={base:confirmed.plan,planId:confirmed.plan.id};setBase(confirmed.plan);
      }
    });
    introductionSave.current=task;return task;
  }
  async function submitConsultation(contact:NonNullable<BlissRoomPlanPayload['contact']>,answers:QuickAnswers,snapshot:ConfiguratorSnapshot,photo?:PhotoPreviewDraft,completedStep:6|7=7){
    await saveIntroduction(contact,answers,completedStep,completedStep===7&&answers.consultation?.intent==='designer'?'custom_inquiry':'progress');
    const current=introduction.current;
    const catalog=customPurchaseLayout(answers.consultation?.direction,answers.consultation?.inspirationId,snapshot);
    const result=await saveBlissRoomPlan({...current.base,layout:{...current.base.layout,...(catalog??{bunkModelId:null,bunkModelLabel:null,configuratorSnapshot:snapshot,rooms:answers.consultation && answers.consultation.entryMode !== "transformation" ? consultationRoomSnapshots(answers.consultation,answers.bunks).map((room,i)=>({...room,id:`consultative-room-${i}`,configuratorSnapshot:{...room.configuratorSnapshot,quoteSettings:snapshot.quoteSettings}})) : [{id:'consultative-room',name:'Your Bliss room',configuratorSnapshot:snapshot}]}),consultativePhoto:photo,consultativeJourney:answers.consultation} as any},current.planId);
    if(!result.plan)throw Error(result.error||'Your brief was saved, but we could not attach the room. Please retry.');
    introduction.current={base:result.plan,planId:result.plan.id};setBase(result.plan);setPlanId(result.plan.id);
    return result.plan.id;
  }
  function startPhoto(answers:QuickAnswers){
    if(rooms.current.length>1||rooms.current.some(r=>r.views)){loadRoom(rooms.current.find(r=>r.id===activeRoom.current)??rooms.current[0],'photo');return;}
    const preferred=quickChoices(answers);const choice=preferred[0]??0;
    setPreferredChoices(preferred);
    const configured=quickBunks(answers);setInitialBunks(configured);setInitialUpgrades(Object.fromEntries(configured.map(b=>[b.choice,b.upgrades])));setFinishes(configured.map(b=>b.finish));setColorCodes(configured.map(b=>b.customColorCode));
    setInitialChoice(choice);
    if(draft.current){const updated=reconcilePhotoBunks(draft.current,configured);draft.current=updated;setInitialDraft(updated);try{sessionStorage.setItem(DRAFT,JSON.stringify(updated))}catch{}}else setInitialDraft(undefined);
    if(!draft.current&&configured.length>1){
      const views=configured.map((b,i)=>({id:`view-${i+1}`,name:i?'Location to choose':'This wall',bunks:[b],bunkNumbers:[i+1]}));
      const room=withRoomViews({id:activeRoom.current,name:'Bunk room 1',bunks:configured},views,views[0].id);
      rooms.current=[room];loadRoom(room,'photo');return;
    }
    choices.current=configured.map(b=>b.choice);setMode('photo');window.scrollTo(0,0);
  }
  function startOrder(answers:QuickAnswers){if(rooms.current.length>1||rooms.current.some(r=>r.views)){loadRoom(rooms.current.find(r=>r.id===activeRoom.current)??rooms.current[0],'order');return;}const configured=quickBunks(answers),selected=configured.map(b=>b.choice);setUpgrades(configured.map(b=>b.upgrades));setFinishes(configured.map(b=>b.finish));setColorCodes(configured.map(b=>b.customColorCode));choices.current=selected;setSelectedChoices(selected);setMode('order');window.scrollTo(0,0);}
  const receipt =
    params.get("review_session") ||
    (() => {
      try {
        return sessionStorage.getItem("bliss_design_receipt") || "";
      } catch {
        return "";
      }
    })();
  useEffect(() => {
    let active = true;
    void (async () => {
      setChecking(true);
      setAccess(false);
      setError("");
      try {
        const auth = await requestDeadline(()=>supabase.auth.getSession(),15000);
        const token = auth.data.session?.access_token;
        if (receipt || token) {
          const {r,data} = await requestDeadline(async signal=>{const r = await fetch(
            `/api/design-workspace?session_id=${encodeURIComponent(receipt)}`,
            { signal, headers: token ? { Authorization: `Bearer ${token}` } : {} },
          );
          const data = await r.json();return {r,data}});
          if (!active) return;
          if (r.ok && data.access && data.staff && location.pathname.startsWith("/designer")) {
            setAccess(true);
            setMode("workspace");
            if (receipt)
              try {
                sessionStorage.setItem("bliss_design_receipt", receipt);
              } catch {}
            if (data.planId) {
              setPlanId(data.planId);
              if (params.get("plan_id") !== data.planId) {
                const q = new URLSearchParams(location.search);
                q.set("plan_id", data.planId);
                q.delete("new");
                navigate(
                  { pathname: location.pathname, search: q.toString() },
                  { replace: true },
                );
                return;
              }
            }
          } else if (r.ok && data.reviewPaid) {
            setMode("review-paid");
            return;
          } else {
            try {
              sessionStorage.removeItem("bliss_design_receipt");
            } catch {}
            if (params.has("review_session"))
              setError(data.error || "Could not verify your payment.");
          }
        }
        const id = params.get("plan_id") || resume.current?.planId;
        if (id) {
          const p = await fetchBlissRoomPlan(id);
          if (active && p) {
            setBase(p);
            setPlanId(p.id);
            const serverRooms=numberPurchaseBunks(restorePurchaseRooms(p.layout));
            const local=resume.current?.planId===p.id?resume.current:undefined;
            const restored=local?local.rooms.map(room=>{const server=serverRooms.find(r=>r.id===room.id);return local.photosOmitted?{...room,photoPreview:server?.photoPreview,views:room.views?.map(v=>({...v,photoPreview:server?.views?.find(s=>s.id===v.id)?.photoPreview}))}:room}):serverRooms;
            if(restored.length){
              rooms.current=restored;
              const stored=restored.find(r=>r.id===(local?.activeRoomId??(p.layout as any)?.activePhotoRoomId))??restored[0];
              activeRoom.current=stored.id;setActiveRoomId(stored.id);
              const view=activeRoomView(stored);activeView.current=view.id;setActiveViewId(view.id);
              draft.current=view.photoPreview;setInitialDraft(view.photoPreview);
              choices.current=view.bunks.map(b=>b.choice);setSelectedChoices(choices.current);
              upgrades.current=view.bunks.map(b=>b.upgrades);setFinishes(view.bunks.map(b=>b.finish));setColorCodes(view.bunks.map(b=>b.customColorCode));setInitialBunks(view.bunks);
              if(restored.length>1||stored.views)setMode((local?.mode??(p.layout as any)?.photoPurchaseMode)==='order'?'order':'photo');
            }
          }
        }
      } catch {
        if (active) setError("Could not reconnect your room. Please retry.");
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [location.search, attempt]);
  async function persist(selected=choices.current,next=currentRooms(selected),selectedRoom=activeRoom.current,lead?:{contact:StudioContact;quoteRequestId:string}){
    const savedRooms=savedPurchaseRooms(next);
    const payload:BlissRoomPlanPayload={...base,...(lead?{contact:lead.contact}:{}),attribution:{...base.attribution,...(lead?{quoteRequestId:lead.quoteRequestId}:{}),source:'photo_tool'},layout:{...base.layout,
      configuratorSnapshot:savedRooms[0].configuratorSnapshot,rooms:next.length===1?savedRooms.map(({photoPreview,...room})=>room):savedRooms,
      activePhotoRoomId:selectedRoom,photoPurchaseMode:mode==='order'?'order':'photo',
      layoutNotes:'Photo visualization only. Room measurements and installation fit require approval before production.',
      // Avoid duplicating full-resolution photos for every multi-room save.
      photoPreview:next.length===1?next[0].photoPreview??null:null,
    } as any};
    const saved=await saveBlissRoomPlan(payload,planId);
    if(!saved.plan)throw Error(saved.error||'Could not save your rooms. Please retry.');
    rooms.current=next;refreshRooms(n=>n+1);
    setBase({...base,...saved.plan,layout:saved.plan.layout??payload.layout,contact:saved.plan.contact??base.contact});setPlanId(saved.plan.id);
    remember(next,selectedRoom,saved.plan.id);return saved.plan.id;
  }

  async function requestQuoteCall(contact: StudioContact) {
    let quoteRequestId = String(introduction.current.base.attribution?.quoteRequestId || base.attribution?.quoteRequestId || '');
    if (!quoteRequestId) {
      quoteRequestId = await captureStudioLead(contact, { notifyTeam: true });
      introduction.current.base = { ...introduction.current.base, attribution: { ...introduction.current.base.attribution, quoteRequestId } };
    }
    const savedId = await persist(choices.current, currentRooms(), activeRoom.current, { contact, quoteRequestId });
    const synced = await syncFunnelLeadToSalesInbox({
      quoteRequestId,
      stage: 'designer_conversation_requested',
      notifyQuoteRequest: true,
      source: 'bliss_design_studio',
      contact: { name: contact.name, email: contact.email, phone: contact.phone, projectZip: contact.zip },
      projectContext: { roomPlanId: savedId, productLine: 'basic', verificationCallRequested: true },
      summary: 'Customer requested a brief call to verify project details and contact information before receiving a quote.',
    });
    if (!synced.success) throw Error('Your room is saved, but the call request could not reach Bliss. Please retry.');
    setQuoteRequested(true);
  }

  async function review(selected = choices.current) {
    setSaving(true);
    setError("");
    try {
      await persist(selected);
      setInitialDraft(draft.current);
      setMode("review");
      window.scrollTo(0, 0);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }
  const changeDraft = (d: PhotoPreviewDraft,roomId=activeRoomId,viewId=activeViewId,generation=photoGeneration.current) => {
    if(generation!==photoGeneration.current||roomId!==activeRoom.current||viewId!==activeView.current)return;
    draft.current=d;
    upgrades.current=d.beds.map(b=>b.upgrades??{});setFinishes(d.beds.map(b=>b.finish??'gloss_white'));setColorCodes(d.beds.map(b=>b.customColorCode));
    choices.current=d.beds.map(b=>b.choice);
    const prior=rooms.current.find(r=>r.id===roomId);
    const baseRoom=prior??{id:roomId,name:'Bunk room 1',bunks:draftBunks(d)};
    const room=updateRoomView(baseRoom,{...activeRoomView(baseRoom),bunks:draftBunks(d),photoPreview:d});
    rooms.current=rooms.current.length?rooms.current.map(r=>r.id===roomId?room:r):[room];
    window.clearTimeout(draftTimer.current);
    draftTimer.current=window.setTimeout(()=>{remember(rooms.current);if(rooms.current.length===1&&!rooms.current[0].views)try{sessionStorage.setItem(DRAFT,JSON.stringify(d))}catch{}},350);
  };
  useEffect(()=>{
    if(mode!=='order'||checking)return;
    const next=currentRooms(selectedChoices);rooms.current=next;remember(next);
  },[mode,checking,selectedChoices,finishes,colorCodes]);
  useEffect(()=>()=>window.clearTimeout(draftTimer.current),[]);

  if (checking)
    return (
      <main className="bpj">
        <p role="status">Opening your room…</p>
      </main>
    );
  if (mode === "review-paid")
    return <main className="bpj"><section className="bpj-review"><img className="bpj-header-logo" src="/bliss-bedrooms-logo-transparent.png" alt="Bliss Bedrooms" width="500" height="326"/><h1>Your Room Review is reserved.</h1><p>Meet with your Bliss Design &amp; Sales Consultant to review your room, explore your options, and choose your next step.</p><a className="bpj-primary" href={paidCalendlySchedulePath({sessionId:receipt})}>Schedule my Room Review</a><p>Your designer will prepare the floor plans and measurements with you.</p><button className="bpj-link" onClick={()=>setMode("photo")}>Back to my room preview</button></section></main>;
  if (access && mode === "workspace")
    return (
      <>
        <div className="bpj-paid">
          <div>
            <h2>Your designer workspace</h2>
            <p>
              Upload floor plans, enter measurements and explore your room with
              Bliss.
            </p>
          </div>
          {receipt && (
            <a href={paidCalendlySchedulePath({ sessionId: receipt })}>
              Schedule my Room Review →
            </a>
          )}
          <button className="bpj-link" onClick={() => setMode("photo")}>
            Try the free photo tool
          </button>
        </div>
        <Suspense fallback={<p>Opening your planner…</p>}>
          <Planner designerWorkspace />
        </Suspense>
      </>
    );
  if (mode === "review")
    return (
      <main className="bpj">
        <RoomReviewCheckout
          planId={planId}
          contact={base.contact}
          onBack={() => setMode(draft.current?.photo ? "photo" : "welcome")}
        />
      </main>
    );
  if (quoteContactOpen) {
    if (quoteRequested) return <main className="bpj"><section className="bpj-review">
      <img className="bpj-header-logo" src="/bliss-bedrooms-logo-transparent.png" alt="Bliss Bedrooms" width="500" height="326"/>
      <h1>Your request is with Bliss.</h1>
      <p>We’ll call to verify your project details and contact information, then send your quote. A representative will be assigned to follow up.</p>
      <a className="bpj-primary" href="tel:+18508120704">Call for Price</a>
      <button className="bpj-link" onClick={() => setQuoteContactOpen(false)}>Back to my room</button>
    </section></main>;
    return <><StudioContactStart verificationCall initial={base.contact ?? {}} continueLabel="Request my quote →" onContinue={requestQuoteCall}/>
      <button className="bpj-link" onClick={() => setQuoteContactOpen(false)}>Back to my room</button></>;
  }
  const sleepGoal=savedSleepGoal((base.layout as any)?.quickAnswers);
  if(setup&&(mode==='photo'||mode==='order'))return <BunkSetupWizard sleepGoal={sleepGoal} rooms={rooms.current} roomId={setup.source?.roomId??activeRoomId} initial={setup.bunk} number={setup.number} moving={!!setup.source} photoOptional={!setup.source||mode==='order'} onCancel={()=>{setSetup(null);resetPhoto();window.scrollTo(0,0)}} onSave={completeSetup}/>;
  if(mode==='order'||(mode==='photo'&&draft.current?.step===5)){
    const all=currentRooms(),price=estimatePlanRange(all.map(purchaseRoomSnapshot),'basic',getPricing());
    return <><CompactPricing onRequestQuote={()=>{setQuoteRequested(false);setQuoteContactOpen(true);}} sleepGoal={sleepGoal} proposal={price.proposal} selectedPackage={checkoutPackage()} rooms={all} total={price.high} busy={saving} error={error} onChange={changePricedBunk} onAdd={()=>beginSetup()} onMove={beginSetup} onRemove={removePricedBunk} onBack={leavePricing} onApprove={approveOrder} onReview={()=>void review()}/>
      {checkout&&planId&&<Suspense fallback={<p>Opening checkout…</p>}><Checkout orderConsent={orderConsent} orderReviewed planId={planId} initialContact={base.contact} initialPackage={checkoutPackage()} productLine="basic" snapshots={orderSnapshots()} roomNames={all.map(r=>r.name)} onClose={()=>setCheckout(false)}/></Suspense>}</>;
  }
  if (mode === "photo")
    return (
      <>
        <nav className="bpj-photo-nav" aria-label="Bliss Bedrooms">
          <button
            className="bpj-brand"
            aria-label="Bliss Bedrooms home"
            onClick={() => {
              setInitialDraft(draft.current);
              setMode("welcome");
            }}
          >
            <img src="/bliss-bedrooms-logo-transparent.png" alt="Bliss Bedrooms" width="500" height="326" />
          </button>

        </nav>
        {roomNavigation}
        {error && <p className="bpj-room-error" role="alert">{error}</p>}
        <Suspense fallback={<p>Opening your photo preview…</p>}>
          <Photo
                    requireVerificationCall
            key={`${activeRoomId}:${activeViewId}:${photoRevision}`}
            roomName={customerRoomName(base.contact?.name,rooms.current.find(r=>r.id===activeRoomId)?.name)}
            onAddBunk={()=>beginSetup()}
            nextBunkNumber={visibleRooms.flatMap(r=>roomViews(r).filter(v=>!v.photoPreview?.photo&&!(r.id===activeRoomId&&v.id===activeViewId)).map(v=>v.bunkNumbers?.[0]))[0]}
            onNextBunk={()=>{const room=visibleRooms.find(r=>roomViews(r).some(v=>!v.photoPreview?.photo&&!(r.id===activeRoomId&&v.id===activeViewId)));const view=room&&roomViews(room).find(v=>!v.photoPreview?.photo&&!(room.id===activeRoomId&&v.id===activeViewId));if(room&&view)beginSetup({roomId:room.id,viewId:view.id,index:0})}}

            onChangeLocation={index=>beginSetup({roomId:activeRoomId,viewId:activeViewId,index})}
            onRemoveBunk={currentRoom.bunks.length>1?removeViewBunk:undefined}
            bunkNavigation={bunkNavigation}
            bunkNumbers={currentView.bunkNumbers}
            photoContext={photoContext}
            roomBusy={saving}
            otherRoomBunks={currentRooms().reduce((n,r)=>n+r.bunks.length,0)-choices.current.length}
            customerName={base.contact?.name}
            initialDraft={initialDraft}
            initialChoice={initialChoice}
            preferredChoices={preferredChoices}
            initialUpgrades={initialUpgrades}
            initialBunks={initialBunks}
            onDraftChange={d=>changeDraft(d,activeRoomId,activeViewId,photoRevision)}
            onPhoto={() => {}}
            onBack={() => {
              setInitialDraft(draft.current);
              setMode("welcome");
            }}
            onContinue={(selected,extras) => {setUpgrades(extras);choices.current=selected;setSelectedChoices(selected);setMode("order");window.scrollTo(0,0);}}
            onReview={(selected,extras) => {setUpgrades(extras);void review(selected)}}
            getEstimate={(selected,extras) => {
              const price=estimatePlanRange(orderSnapshots(selected,extras),'basic',getPricing());
              return {low:price.high,high:price.high};
            }}
            onAcceptPrice={async (selected,extras) => {
              setUpgrades(extras);
              choices.current=selected;setSelectedChoices(selected);
              validateOrder();await persist(selected);setMode('order');window.scrollTo(0,0);
            }}
          />
          {checkout && planId && (
            <Checkout
              planId={planId}
              initialContact={base.contact}
              initialPackage={checkoutPackage()}
              productLine="basic"
              snapshots={orderSnapshots()} roomNames={currentRooms().map(r=>r.name)}
              onClose={() => setCheckout(false)}
            />
          )}
        </Suspense>
      </>
    );
  const priorAnswers=(base.layout as any)?.quickAnswers;
  // Existing saved plans retain their established order/resume path. New visitors use the consultative journey.
  if(params.get('plan_id')&&priorAnswers&&!priorAnswers.consultation)return <BlissQuickStart contact={base.contact} initialAnswers={priorAnswers} onSave={saveIntroduction} onPhoto={startPhoto} onOrder={startOrder} onReview={()=>void review()} />;
  return <BlissConsultativeJourney requireVerificationCall contact={base.contact} initialAnswers={priorAnswers} initialPhoto={(base.layout as any)?.consultativePhoto} onSubmit={submitConsultation}/>;
}
